import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3, Monitor, Smartphone, Tablet } from 'lucide-react';
import { SectionHeader } from './shared/SectionHeader';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';
import { METRIC_OPTIONS } from '../utils/constants';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card rounded-xl px-3 py-2 text-xs shadow-lg">
      <p className="text-gray-500 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-semibold" style={{ color: p.stroke }}>
          {p.name}: {typeof p.value === 'number' && p.value > 1000 ? formatCurrencyCompact(p.value) : formatStat(p.value)}
        </p>
      ))}
    </div>
  );
};

export function PerformanceCanvas({
  activeMetric, onMetricChange, generateChartData,
  googleAdsSummary, metaAdsSummary, criteoAdsSummary,
  gaDevicePerf, comparisonAggregates,
}) {
  const chartData = useMemo(() => {
    const raw = generateChartData(activeMetric);
    if (!raw?.labels) return [];
    return raw.labels.map((label, i) => ({
      name: label.split(' | ')[0],
      current: raw.current[i] || 0,
      previous: raw.previous[i] || 0,
    }));
  }, [activeMetric, generateChartData]);

  // Platform bars data
  const platforms = [
    googleAdsSummary?.totalCost > 0 && {
      name: 'Google Ads',
      revenue: googleAdsSummary.totalGoogleAdsRevenue || 0,
      roas: googleAdsSummary.roas || 0,
      color: '#3B82F6',
    },
    metaAdsSummary?.totalSpend > 0 && {
      name: 'Meta Ads',
      revenue: metaAdsSummary.totalMetaRevenue || 0,
      roas: metaAdsSummary.roas || 0,
      color: '#6366F1',
    },
    criteoAdsSummary?.totalCost > 0 && {
      name: 'Criteo',
      revenue: criteoAdsSummary.totalCriteoRevenue || 0,
      roas: criteoAdsSummary.roas || 0,
      color: '#F59E0B',
    },
  ].filter(Boolean);

  const maxRevenue = Math.max(...platforms.map(p => p.revenue), 1);

  // Device data
  const totalDeviceSessions = (gaDevicePerf || []).reduce((s, d) => s + (d.sessions || 0), 0);
  const deviceIcons = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };
  const deviceColors = { mobile: '#3B82F6', desktop: '#6366F1', tablet: '#F59E0B' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
      className="mb-6"
    >
      <SectionHeader title="Performance" icon={BarChart3} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart - 2/3 */}
        <div className="lg:col-span-2 glass-card rounded-2xl p-5">
          {/* Metric pills */}
          <div className="pill-tab-container inline-flex mb-4">
            {METRIC_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => onMetricChange(opt.key)}
                className={`pill-tab text-xs ${activeMetric === opt.key ? 'pill-tab-active' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <defs>
                  <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={45} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="previous" name="Previous" stroke="#9CA3AF" strokeWidth={1.5} strokeDasharray="4 4" fill="transparent" dot={false} />
                <Area type="monotone" dataKey="current" name="Current" stroke="#3B82F6" strokeWidth={2} fill="url(#colorCurrent)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Platform Bars - 1/3 */}
        <div className="glass-card rounded-2xl p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Platform Revenue</h3>
          <div className="space-y-4">
            {platforms.map((p) => (
              <div key={p.name}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-700">{p.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-900">{formatCurrencyCompact(p.revenue)}</span>
                    <span className="metric-badge-up text-[10px]">{p.roas.toFixed(1)}x</span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(p.revenue / maxRevenue) * 100}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="h-full rounded-full"
                    style={{ background: p.color }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Device split */}
          {gaDevicePerf && gaDevicePerf.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Device Split</h4>
              {/* Stacked bar */}
              <div className="h-3 rounded-full overflow-hidden flex mb-3">
                {gaDevicePerf.map((d) => {
                  const pct = totalDeviceSessions > 0 ? (d.sessions / totalDeviceSessions) * 100 : 0;
                  const cat = (d.deviceCategory || d.key || '').toLowerCase();
                  return (
                    <div
                      key={cat}
                      style={{ width: `${pct}%`, background: deviceColors[cat] || '#9CA3AF' }}
                      className="h-full"
                    />
                  );
                })}
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-3">
                {gaDevicePerf.map((d) => {
                  const cat = (d.deviceCategory || d.key || '').toLowerCase();
                  const pct = totalDeviceSessions > 0 ? ((d.sessions / totalDeviceSessions) * 100).toFixed(0) : 0;
                  const DeviceIcon = deviceIcons[cat] || Monitor;
                  return (
                    <div key={cat} className="flex items-center gap-1">
                      <DeviceIcon size={12} style={{ color: deviceColors[cat] || '#9CA3AF' }} />
                      <span className="text-[10px] text-gray-500 capitalize">{cat}</span>
                      <span className="text-[10px] font-semibold text-gray-700">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
